import { h, Component } from 'preact';
import { joinClasses } from 'utils/utils';
import { alert, confirm, showModal } from 'utils/modal';
import { updateUsers } from 'utils/users';
import { getAllUsers, post } from 'utils/api';
import { allRoles, canUser, getRoleName, getRoleLevel } from 'utils/roles';

import Button from 'components/button/button';
import Dropdown from 'components/dropdown/dropdown';
import UserAvatar from 'components/userAvatar/userAvatar';

import s from 'components/users/users.sss';

export default class Users extends Component {

	constructor(props) {
		super(props);
		this.state = {
			users: [],
			loading: true,
			changes: {},
			roleFilter: '',
		};

		this.cancel = this.cancel.bind(this);
		this.onSaveClick = this.onSaveClick.bind(this);
		this.inviteUser = this.inviteUser.bind(this);
		this.updateRoleFilter = this.updateRoleFilter.bind(this);
		this.updateUserPermissions = this.updateUserPermissions.bind(this);
		this.renderUser = this.renderUser.bind(this);
		this.canEditRole = this.canEditRole.bind(this);
	}

	async componentDidMount() {
		try {
			await this.refreshUsers();
			this.setState({ loading: false });
		} catch (err) {
			console.error('Failed to fetch users:', err);
			// TODO: display error in UI
			this.setState({ loading: false, loadError: true });
		}
	}

	async refreshUsers() {
		const users = await getAllUsers();
		this.setState({ users: users.data.sort((a, b) => a.email?.localeCompare(b.email || '') || 1) });
	}

	cancel() {
		this.base.close();
	}

	async save() {
		if (this.state.saving) return;
		this.setState({ saving: true, saveError: false });
		try {
			await updateUsers(this.state.changes);
			this.setState({ saving: false, changes: {} });
			this.base.close();
		} catch (e) {
			console.error(e);
			this.setState({ saving: false, saveError: true });
		}
	}

	async onSaveClick(e) {
		const deletions = Object.values(this.state.changes).filter(role => role === 'DELETE').length;
		if (deletions) {
			const confirmed = await confirm(`Your changes will delete ${deletions} user${deletions === 1 ? '' : 's'}. Are you sure?`, {
				title: 'Confirmation',
				button: 'Delete',
				danger: true,
			});
			if (!confirmed) return;
		}
		return this.save();
	}

	async inviteUser(e) {
		e.preventDefault();
		if (this.state.inviting) return;
		const form = e.target;
		const data = Object.fromEntries(new FormData(form));
		const email = data['invite-email']?.trim().toLowerCase();
		if (!email) return;
		const role = data.role;
		if (this.state.users.some(user => user.email === email)) {
			await alert('This email address already has access', { title: 'Error inviting user' });
			return;
		}
		this.setState({ inviting: true });
		try {
			await post('/users/invite', { email, role });
			await this.refreshUsers();
			this.setState({ inviting: false, inviteError: false, inviteSucess: true });
			form.reset();
		} catch (e) {
			console.error(e);
			this.setState({ inviting: false, inviteError: e.message });
		}
	}

	getInviteMessage() {
		const { inviteSucess, inviteError } = this.state;
		if (inviteSucess) {
			return <div class={joinClasses(s.inviteMessage, s.successMsg)}>User added</div>;
		}
		if (inviteError) {
			// TODO: error might have happened for a different reason (e.g. server error)
			return <div class={joinClasses(s.inviteMessage, s.errorMsg)}>User with this email already exists</div>;
		}
		return <div class={s.inviteMessage}>NBCU SSO & CMS group access is required before adding users</div>;
	}

	updateRoleFilter(e) {
		this.setState({ roleFilter: e.target.value });
	}

	canEditRole(role) {
		const userLevel = getRoleLevel();
		if (!canUser('user_manage')) return false;
		if (userLevel > getRoleLevel(role)) return true;
		if (userLevel === 5) return true; // super_admin can edit any role
		return false;
	}

	updateUserPermissions(e) {
		const user = e.target.details.user;
		const role = e.target.value;
		const changes = this.state.changes;
		if (user.role === role) {
			delete changes[user.id];
		} else {
			changes[user.id] = role;
		}
		this.setState({ changes });
	}

	renderUser(user) {
		const userRole = this.state.changes[user.id] || user.role;
		const pending = user.last_login === null;
		return (
			<div class={joinClasses(s.user, pending && s.pending)} key={user.id}>
				<UserAvatar user={user} class={s.avatar} key="avatar" />
				<div class={s.details} key="details">
					<div class={s.name}>{user.name}</div>
					<div class={s.email}>{user.email?.replace('@', '\u200b@')}</div>
				</div>
				{this.canEditRole(userRole) ? (
					<Dropdown class={s.role} name={`user-${user.id}-role`} value={userRole} align="right" onChange={this.updateUserPermissions} details={{ user }}>
						<div class={s.title}>Set permissions</div>
						<div class={s.options}>
							{allRoles.filter(this.canEditRole).map(role => <option value={role}>{getRoleName(role)}</option>)}
						</div>
						<hr />
						<option class={s.remove} value="DELETE">Remove user</option>
					</Dropdown>
				) : (
					<div class={joinClasses(s.role, s.readOnly)} key="role">{getRoleName(userRole)}</div>
				)}
			</div>
		);
	}

	render(props, state) {
		let displayedUsers = state.users;
		if (this.state.roleFilter) {
			displayedUsers = displayedUsers.filter(user => user.role === state.roleFilter);
		}
		return (
			<dialog class={s.wrapper} id="user">
				<Button class={s.close} icon="cancel" iconSize="large" onClick={this.cancel} />
				<h2>Users</h2>
				<div class={joinClasses(s.invite, state.inviting && s.loading)}>
					<h3>Add NBCU onboarded user</h3>
					<form onSubmit={this.inviteUser} inert={state.inviting}>
						<div class={s.inputWrapper}>
							<input type="email" name="invite-email" placeholder="email@nbcuni.com" required autofocus />
							<Dropdown id="user-invite-role" name="role" align="right" initialValue="editor">
								{allRoles.filter(this.canEditRole).map(role => <option value={role}>{getRoleName(role)}</option>)}
							</Dropdown>
						</div>
						<Button primary type="submit" class={s.submitInvite}>Add</Button>
					</form>
					{this.getInviteMessage()}
				</div>
				<div class={s.users}>
					<div class={s.filter}>
						Showing
						<Dropdown class={s.role} id="users-filter-role" name="filter-role" value="" onChange={this.updateRoleFilter}>
							<option value="">All users</option>
							{allRoles.map(role => <option value={role}>{getRoleName(role)}</option>)}
						</Dropdown>
					</div>
					<h3>Who has access</h3>
					<div class={s.list}>
						{state.loading && (
							<div class={s.loadingWrapper}>
								<div class={s.spinner} />
							</div>
						)}
						{!state.loading && displayedUsers && displayedUsers.map(this.renderUser)}
					</div>
				</div>
				<div class={s.buttons}>
					<Button onClick={this.cancel}>Cancel</Button>
					<Button primary onClick={this.onSaveClick} disabled={state.saving || !Object.keys(state.changes).length}>Save Changes</Button>
				</div>
			</dialog>
		);
	}
}

export async function showUsers() {
	return showModal(Users);
}